import { MouseEvent, useState } from 'react';
import { Checkbox, TableCell, TableRow, Button } from '@mui/material';
import { IPagingData, MediaInfoDto } from '@sr/dto';
import MenuParams from 'shared/ui/menu-params';
import { IconButton } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import { GridLayout } from 'shared/ui/grid-layout';
import { RowMenu, useMenu } from 'shared/ui/row-menu';
import { LinkToMedia } from 'shared/ui/links/link-to-media';
import { IRemotePagedCollection } from 'utils/remote-paged-collection.hook';
import { mediaListApi } from './api-slice';
import { useSnack } from 'shared/ui/snack';
import useConfirmDialog from 'hooks/confirm-dialog.hook';

type Props = {
  collection: IRemotePagedCollection<MediaInfoDto>,
  paging?: IPagingData;
  onChangePaging?: (paging: IPagingData) => void;
  onMediaDeleteClick: (media: MediaInfoDto) => void,
  readonly?: boolean
};

export const MediaList = ({ collection, paging, onChangePaging, onMediaDeleteClick, readonly = false }: Props) => {
  const showConfirm = useConfirmDialog();
  const { showSuccessDelete, showFailedDelete } = useSnack();
  const [checkedItems, setCheckedItems] = useState<number[]>([]);

  const [deleteMedias, { isLoading: isDeleting }] = mediaListApi.useDeleteMediasMutation();
  
  const { openMenu, menuData } = useMenu<MediaInfoDto>({
    deleteHandler: onMediaDeleteClick
  });

  const handleToggleAll = (e: any, checked: boolean) => {
    if (checked) {
      setCheckedItems(collection.collection.items.map((x) => x.id));
    } else {
      setCheckedItems([]);
    }
  };

  const isAllChecked = () => {
    if (collection.collection.items.length === 0) return false;
    return checkedItems.length === collection.collection.items.length;
  };

  const isIndeterminate = () => {
    return checkedItems.length > 0 && checkedItems.length < collection.collection.items.length;
  };

  const handleCheckChange = (id: number, checked: boolean) => {
    if (checked) {
      setCheckedItems(prev => [...prev, id]);
    } else {
      setCheckedItems(prev => prev.filter(itemId => itemId !== id));
    }
  };

  const handleBulkDelete = () => {
    if (checkedItems.length > 0) {
      showConfirm({
        content: `Удалить выбранные СМИ (${checkedItems.length})?`
      })
        .then(async() => {
          try {
            await deleteMedias(checkedItems).unwrap();
            setCheckedItems([]);
            showSuccessDelete();
          } catch (error) {
            const errorObj = error instanceof Error ? error : new Error(String(error));
            showFailedDelete(errorObj);
          }
        });
    }
  };

  const getColumns = () => {
    const cols: any[] = ['Название', 'Местоположение', 'Тип'];
    if (!readonly) {
      cols.unshift(          {
            label: '',
            align: 'left',
            padding: 'checkbox'
          })
      cols.push('');
    }
    return cols
  }

  return (
    <>
      {!readonly && <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
        <Checkbox 
          onChange={handleToggleAll} 
          checked={isAllChecked()} 
          indeterminate={isIndeterminate()} 
        />
        <Button 
          size="small" 
          variant="contained" 
          startIcon={<DeleteIcon />} 
          onClick={handleBulkDelete}
          disabled={checkedItems.length === 0 || isDeleting}
          color="error"
        >
          {isDeleting ? 'Удаление...' : `Удалить выбранные (${checkedItems.length})`}
        </Button>
      </div>}


      <GridLayout<MediaInfoDto>
        columns={getColumns()}
        items={collection.collection.items}
        totalItemsCount={collection.collection.totalItemsCount}
        isLoading={collection.isLoading}
        error={collection.error}
        pagingData={paging}
        onChangePaging={onChangePaging}
        itemRender={(item) =>
          <MediaList.Row
            key={item.id}
            readonly={readonly}
            mediaData={item}
            checked={checkedItems.includes(item.id)}
            onCheckChange={(checked) => handleCheckChange(item.id, checked)}
            onMenuClick={openMenu} />}
        noItemsText='Список СМИ пуст'
      />

      <RowMenu menuData={menuData} />
    </>);
};

type RowProps = {
  mediaData: MediaInfoDto,
  checked: boolean,
  readonly?: boolean,
  onCheckChange: (checked: boolean) => void,
  onMenuClick: (menuParams: MenuParams<MediaInfoDto>) => void;
};

MediaList.Row = ({readonly = false, ...props}: RowProps) => {
  const { mediaData, checked, onCheckChange, onMenuClick } = props;

  const handleMenuClick = (e: MouseEvent<HTMLElement>) => {
    onMenuClick({ anchor: e.currentTarget, target: mediaData });
  };

  const getLocationText = () => {
    if (mediaData.country && !mediaData.region && !mediaData.city) {
      return `${mediaData.country?.name || mediaData.country}`;
    }
    if (mediaData.region && !mediaData.city) {
      return `${mediaData.region?.name || mediaData.region}`;
    }
    if (mediaData.city) {
      return `${mediaData.city?.name || mediaData.city}`;
    }
    return 'Местоположение не указано';
  };

  return (
    <>
      <TableRow key={mediaData.id}>
        {!readonly && <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            checked={checked}
            onChange={(e, checked) => onCheckChange(checked)}
          />
        </TableCell>}
        <TableCell component="th" scope="row">
          <LinkToMedia media={mediaData} >
            {mediaData.name}
          </LinkToMedia>
          {mediaData.childrenMedia && mediaData.childrenMedia.length > 0 && (
            <span style={{ color: '#666', fontSize: '0.75em', marginLeft: '4px' }}>
                  (Материнское СМИ)
            </span>
          )}
          {mediaData.parentMedia && (
            <span style={{ color: '#666', fontSize: '0.75em', marginLeft: '4px' }}>
                  (СМИ является дочерним для{' '}
              <LinkToMedia media={mediaData.parentMedia}>
                {mediaData.parentMedia.name}
              </LinkToMedia>
                  )
            </span>
          )}
        </TableCell>
        <TableCell>{getLocationText()}</TableCell>
        <TableCell>{mediaData.mediaType.name}</TableCell>
        {!readonly && <TableCell align="right">
          <IconButton edge="end" onClick={handleMenuClick}><MoreVertIcon /></IconButton>
        </TableCell>}
      </TableRow>
    </>
  );
};
